import React, { createContext, FC, useContext, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { useQuery } from 'react-query'
import * as Sentry from '@sentry/react'
import { AuthApi, efkt } from '@libs/api-v2'
import { isPublicRoute } from 'v2/routes.map'

/**
 * Which shell renders - the pre-Efkt tree or the Efkt one - decided once, above both, for every
 * route. Two inputs, in this order:
 *
 * **1. Is the route public?** A route with no signed-in user has no customer to ask about, so there
 * is nothing to decide and nothing to wait for: it renders the Efkt tree immediately. Anything
 * backend-specific is then the page's own job, decided per record (try Efkt, fall back to datahub),
 * the way `BookingPageContent/hooks.ts` already does it. The list lives in `v2/routes.map.ts` next
 * to the route map it has to stay in step with.
 *
 * **2. Otherwise, `GET /api/auth/migration-status`** - the dual run's one routing question, answered
 * from the token alone (AXIT-672). `customers.MigratedOwners` holds a row per cut-over account and a
 * customer is served by Efkt when any account in their lineage has one, so a row on an Owner moves
 * every branch beneath it and a row on a single branch moves a pilot.
 *
 * **One request, and no branch id anywhere in it.** An earlier design resolved the user's branches
 * first and gated on `branches[0].id`; that could not be answered at all, because booqly holds
 * *retail-core* branch ids and the field mapping one onto an Efkt account is not persisted yet
 * (AXIT-719). Keying on the token's `sub` also means this can be asked before any other request has
 * returned.
 *
 * **Every failure means "stay on the pre-Efkt tree."** A 401 is "not signed in" and never "not
 * migrated"; a 500 is a verdict that could not be reached rather than a false one; a dead network is
 * neither. All three land on the tree the session already works on. Nothing here uses
 * `useErrorBoundary`, deliberately - the backend answers `200 { isMigrated: false }` for an employee
 * token precisely so booqly does not take a 401 to its error boundary and blank the app.
 *
 * **The app holds a loading state until the question is answered** - `pages/_app.tsx` renders nothing
 * while `isLoading` - because rendering one shell and swapping it for the other a moment later is the
 * visible double-blink this exists to prevent. The cache below is what keeps that wait off the common
 * path.
 */

/**
 * This query key is this provider's own, not `modules/common/queries`' shared one. It shares a
 * QueryClient with the pre-Efkt tree (`pages/_app.tsx` mounts one above this provider), so answering
 * a key that tree reads would hand it records resolved by rules it knows nothing about - the one
 * thing the read-only rule exists to prevent.
 */
const SHELL_MIGRATION_QUERY_KEY = 'ui-version-migration-status'

// Last-known answer, read synchronously at mount so a returning visit renders the right shell on the
// very first paint instead of the old one flashing and then being torn down once the request lands.
// Keyed by nothing: the verdict is per customer, and it is cleared the moment nobody is signed in.
const UI_VERSION_CACHE_PREFIX = 'ui.version.'
const LAST_KNOWN_CACHE_KEY = 'last-known'

function readCachedUiVersion(key: string): boolean | undefined {
	if (typeof localStorage === 'undefined') return undefined
	const cached = localStorage.getItem(UI_VERSION_CACHE_PREFIX + key)
	return cached === null ? undefined : cached === 'true'
}

function writeCachedUiVersion(key: string, value: boolean): void {
	if (typeof localStorage !== 'undefined') localStorage.setItem(UI_VERSION_CACHE_PREFIX + key, String(value))
}

// The one explicit clear point: the cached verdict answers for a person, so it goes stale the moment
// nobody is signed in to vouch for it. Detected here rather than in modules/auth/hooks.ts's
// logout()/softLogout(), which are pre-Efkt and read-only.
function clearCachedUiVersions(): void {
	if (typeof localStorage === 'undefined') return
	Object.keys(localStorage)
		.filter(key => key.startsWith(UI_VERSION_CACHE_PREFIX))
		.forEach(key => localStorage.removeItem(key))
}

interface IUiVersionContextType {
	newUiEnabled: boolean
	isLoading: boolean
}

const UiVersionContext = createContext<IUiVersionContextType>({ newUiEnabled: false, isLoading: false })

export const FeatureFlagProvider: FC = ({ children }) => {
	const router = useRouter()
	const isPublic = isPublicRoute(router.pathname)
	const isAuthenticated = AuthApi.isAuthenticated()

	// Stated once and reused: a public route has no customer to ask about, and a signed-out visitor
	// has no token to ask with (the shell's own auth guard redirects them to /login, which is public).
	const needsFlag = !isPublic && isAuthenticated

	const wasAuthenticatedRef = useRef(isAuthenticated)
	useEffect(() => {
		if (wasAuthenticatedRef.current && !isAuthenticated) clearCachedUiVersions()
		wasAuthenticatedRef.current = isAuthenticated
	}, [isAuthenticated])

	const [lastKnownUiVersion] = useState(() => readCachedUiVersion(LAST_KNOWN_CACHE_KEY))

	// The verdict holds for the session - an Owner is cut over as a scheduled event that ends the old
	// session anyway - so this is asked once per page load and never polled.
	const migrationQuery = useQuery(SHELL_MIGRATION_QUERY_KEY, efkt.AuthApi.getCustomerMigrationStatus, {
		enabled: needsFlag,
		// Against `query.client.ts`'s global `retry: false`, and the only query here that overrides it:
		// every other failed read costs one screen, this one costs the whole shell. Retrying is not a
		// softening of the rule below - a failure still means the pre-Efkt tree - it just makes a single
		// dropped response stop being enough to trigger it. Bounded to one, like the client's own 401
		// retry, so a genuinely broken gateway still answers quickly.
		retry: 1,
		onSuccess: ({ isMigrated, resolution }) => {
			// Remember a real verdict for the next visit, so a refresh never waits on this request again.
			// Only on success: persisting a failure's answer would turn one bad request into this browser's
			// standing verdict, which is the opposite of what the cache is for.
			writeCachedUiVersion(LAST_KNOWN_CACHE_KEY, isMigrated)

			// The one place `resolution` surfaces, and the reason the field is carried at all: "why am I
			// still on the old UI" is the question support gets, and this answers it on every Sentry event
			// from the session for free. A tag rather than a log line because nobody reads a user's console.
			Sentry.setTag('shell.migration', resolution)
		},
	})

	// `isMigrated` and nothing else. `resolution` is reported above, never branched on.
	//
	// An error resolves to the pre-Efkt tree rather than to the cached answer: the contract is that any
	// failure means stay on retail-core. Worth knowing what that costs - a migrated customer whose
	// retry also fails is moved to the tree that no longer holds their data, for as long as the failure
	// lasts. Preferring the cache here would be a change to that contract, so it is not made quietly.
	const isSettled = needsFlag && !migrationQuery.isLoading
	const flagAnswer = isSettled ? !!migrationQuery.data?.isMigrated : lastKnownUiVersion

	const value: IUiVersionContextType = isPublic
		? { newUiEnabled: true, isLoading: false }
		: {
				newUiEnabled: !!flagAnswer,
				// Block only while there is nothing to render with. The cached verdict is what keeps a
				// refresh off this path; without it - a browser's first-ever visit - the wait is the point,
				// since the alternative is rendering one shell and tearing it down.
				isLoading: needsFlag && migrationQuery.isLoading && lastKnownUiVersion === undefined,
		  }

	return <UiVersionContext.Provider value={value}>{children}</UiVersionContext.Provider>
}

export const useNewUiEnabled = () => useContext(UiVersionContext)
